import React, { useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  GoogleMap,
  Marker,
  Polyline,
  useJsApiLoader,
} from '@react-google-maps/api';
import { Empty } from 'antd';
import _ from 'lodash';
import moment from 'moment';

import AppStore from 'stores/App';
import AlarmMarker from 'components/AlarmMarker';
import { getLocationPolylines } from '../utils';

import startMarkerUrl from './Icons/startmarker.png';
import endMarkerUrl from './Icons/endmarker.png';

const containerStyle = {
  width: '100%',
  height: '100%',
};

const SingleTrip = ({
  selectedTrip,
  currentMarkerTime,
  showAnalytics,
  showEmissionTable,
  snapToRoad,
  zoom = 11,
}) => {
  const [map, setMap] = useState(null);
  const [polylinePath, setPolylinePath] = useState([]);
  const [alarms, setAlarms] = useState([]);
  const [center, setCenter] = useState(null);
  const [analyticsMarker, setAnalyticsMarker] = useState(null);

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: 'AIzaSyDxDzCBe05AjpvIN1IJOq1u1q2tqzGzHpo',
    libraries: ['geometry'],
  });

  const fetchSnappedPath = useCallback(async () => {
    const polylines = getLocationPolylines(selectedTrip);
    if (!polylines || polylines.length === 0) return [];

    const path = polylines
      .map((p) => `${p.location.latitude},${p.location.longitude}`)
      .join('|');

    try {
      const response = await fetch(
        `https://roads.googleapis.com/v1/snapToRoads?path=${path}&interpolate=true&key=YOUR_GOOGLE_MAPS_API_KEY`
      );
      const data = await response.json();
      return data.snappedPoints.map((pt) => ({
        lat: pt.location.latitude,
        lng: pt.location.longitude,
      }));
    } catch (error) {
      AppStore.addError('Unable to get trip markers');
      return [];
    }
  }, [selectedTrip]);

  useEffect(() => {
    const initializeMapData = async () => {
      let path = [];
      if (snapToRoad) {
        path = await fetchSnappedPath();
      } else {
        path = getLocationPolylines(selectedTrip).map((p) => ({
          lat: parseFloat(p.location.latitude),
          lng: parseFloat(p.location.longitude),
        }));
      }
      setPolylinePath(path);

      if (path.length > 0) {
        setCenter(path[0]);
      }

      const tripAlarms = selectedTrip.alarms || [];
      const alarmMarkers = tripAlarms.map((alarm) => {
        const timestamp = moment(alarm.triggeredAt).unix();
        const closestPoint = path.reduce((prev, curr) =>
          Math.abs(curr.timestamp - timestamp) <
          Math.abs(prev.timestamp - timestamp)
            ? curr
            : prev
        );
        return {
          name: alarm.name,
          lat: closestPoint.lat,
          lng: closestPoint.lng,
          triggeredAt: moment(alarm.triggeredAt).format(
            'dddd, MMMM Do YYYY, h:mm:ss a'
          ),
        };
      });
      setAlarms(alarmMarkers);
    };

    if (isLoaded) {
      initializeMapData();
    }
  }, [isLoaded, selectedTrip, snapToRoad, fetchSnappedPath]);

  useEffect(() => {
    if (!currentMarkerTime || polylinePath.length === 0) return;

    const timestamp = parseInt(String(currentMarkerTime).slice(0, -3), 10);
    const closestPoint = polylinePath.reduce((prev, curr) =>
      Math.abs(curr.timestamp - timestamp) <
      Math.abs(prev.timestamp - timestamp)
        ? curr
        : prev
    );
    setAnalyticsMarker(closestPoint);
  }, [currentMarkerTime, polylinePath]);

  useEffect(() => {
    if (!map || !selectedTrip?.locationStart || !selectedTrip?.locationEnd)
      return;

    const bounds = new window.google.maps.LatLngBounds();

    bounds.extend({
      lat: parseFloat(selectedTrip.locationStart.latitude),
      lng: parseFloat(selectedTrip.locationStart.longitude),
    });

    bounds.extend({
      lat: parseFloat(selectedTrip.locationEnd.latitude),
      lng: parseFloat(selectedTrip.locationEnd.longitude),
    });

    setTimeout(() => {
      map.fitBounds(bounds, { top: 50, bottom: 50, left: 50, right: 50 });
    }, 100);
  }, [map, selectedTrip]);

  if (!isLoaded) {
    return <div>Loading...</div>;
  }

  if (!polylinePath || polylinePath.length === 0) {
    return <Empty description="No available location data" />;
  }

  return (
    <GoogleMap
      mapContainerStyle={containerStyle}
      center={center}
      zoom={zoom}
      //onLoad={(mapInstance) => setMap(mapInstance)}
      options={{
        mapTypeControl: true,
        mapTypeControlOptions: {
          style: window.google.maps.MapTypeControlStyle.HORIZONTAL_BAR,
          position: window.google.maps.ControlPosition.TOP_CENTER,
          mapTypeIds: [
            window.google.maps.MapTypeId.ROADMAP,
            window.google.maps.MapTypeId.SATELLITE,
            window.google.maps.MapTypeId.HYBRID,
          ],
        },
      }}
      onLoad={(mapInstance) => {
        setMap(mapInstance);

        const bounds = new window.google.maps.LatLngBounds();

        if (selectedTrip.locationStart) {
          bounds.extend({
            lat: parseFloat(selectedTrip.locationStart.latitude),
            lng: parseFloat(selectedTrip.locationStart.longitude),
          });
        }

        if (selectedTrip.locationEnd) {
          bounds.extend({
            lat: parseFloat(selectedTrip.locationEnd.latitude),
            lng: parseFloat(selectedTrip.locationEnd.longitude),
          });
        }

        mapInstance.fitBounds(bounds);
      }}
    >
      {/* Polyline */}
      <Polyline
        path={polylinePath}
        options={{
          strokeColor: 'blue',
          strokeOpacity: 0.75,
          strokeWeight: 5,
        }}
      />

      {/* Start Marker */}
      {selectedTrip.locationStart && (
        <Marker
          position={{
            lat: parseFloat(selectedTrip.locationStart.latitude),
            lng: parseFloat(selectedTrip.locationStart.longitude),
          }}
          icon={{
            url: startMarkerUrl,
            scaledSize: new window.google.maps.Size(12, 12),
          }}
        />
      )}

      {/* End Marker */}
      {selectedTrip.locationEnd && (
        <Marker
          position={{
            lat: parseFloat(selectedTrip.locationEnd.latitude),
            lng: parseFloat(selectedTrip.locationEnd.longitude),
          }}
          icon={{
            url: endMarkerUrl,
            scaledSize: new window.google.maps.Size(55, 55),
          }}
        />
      )}

      {/* Alarm Markers */}
      {alarms.map((alarm, index) => (
        <AlarmMarker
          key={index}
          lat={parseFloat(alarm.lat)}
          lng={parseFloat(alarm.lng)}
          name={alarm.name}
          triggeredAt={alarm.triggeredAt}
        />
      ))}

      {/* Analytics Marker */}
      {analyticsMarker && (
        <Marker
          position={analyticsMarker}
          icon={{
            url: startMarkerUrl,
            scaledSize: new window.google.maps.Size(12, 12),
          }}
        />
      )}
    </GoogleMap>
  );
};

SingleTrip.propTypes = {
  selectedTrip: PropTypes.object.isRequired,
  currentMarkerTime: PropTypes.number,
  showAnalytics: PropTypes.bool,
  showEmissionTable: PropTypes.bool,
  snapToRoad: PropTypes.bool,
  zoom: PropTypes.number,
};

export default SingleTrip;
